import { type RefObject, useCallback, useMemo } from 'react';
import {
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';

import gridKeyboardCoordinates from './gridKeyboardCoordinates';
import type { GalleryItem } from './ImageGallery.common';
import { galleryItemKey } from './ImageGallery.common';

type UseGridReorderArgs = {
  images: GalleryItem[];
  onReorder?: (fromIndex: number, toIndex: number) => void;
  isReorderable?: (index: number) => boolean;
  gridRef?: RefObject<HTMLElement | null>;
};

type UseGridReorderReturn = {
  sensors: ReturnType<typeof useSensors>;
  sortableKeys: string[];
  canDrag: (index: number) => boolean;
  onDragEnd: (event: DragEndEvent) => void;
};

const DRAG_ACTIVATION_DISTANCE = 5;
const TOUCH_DRAG_ACTIVATION_DELAY = 200;
const TOUCH_DRAG_TOLERANCE = 5;

const useGridReorder = ({
  images,
  onReorder,
  isReorderable,
  gridRef,
}: UseGridReorderArgs): UseGridReorderReturn => {
  const getColumns = useCallback((): number => {
    const grid = gridRef?.current;

    if (!grid) {
      return 1;
    }

    return getComputedStyle(grid).gridTemplateColumns.split(' ').filter(Boolean).length;
  }, [gridRef]);

  const coordinateGetter = useMemo(() => gridKeyboardCoordinates(getColumns), [getColumns]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: DRAG_ACTIVATION_DISTANCE },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: TOUCH_DRAG_ACTIVATION_DELAY,
        tolerance: TOUCH_DRAG_TOLERANCE,
      },
    }),
    useSensor(KeyboardSensor, { coordinateGetter })
  );

  const canDrag = useCallback(
    (index: number): boolean =>
      index >= 0 && Boolean(onReorder) && (isReorderable?.(index) ?? true),
    [onReorder, isReorderable]
  );

  const sortableKeys = useMemo(
    () => images.filter((_, index) => canDrag(index)).map(galleryItemKey),
    [images, canDrag]
  );

  const indexOfKey = useCallback(
    (key: unknown): number => images.findIndex((image) => galleryItemKey(image) === key),
    [images]
  );

  const onDragEnd = useCallback(
    ({ active, over }: DragEndEvent): void => {
      const fromIndex = indexOfKey(active.id);
      const toIndex = indexOfKey(over?.id);

      if (fromIndex === toIndex || !canDrag(fromIndex) || !canDrag(toIndex)) {
        return;
      }

      onReorder?.(fromIndex, toIndex);
    },
    [indexOfKey, canDrag, onReorder]
  );

  return { sensors, sortableKeys, canDrag, onDragEnd };
};

export default useGridReorder;
